import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import { useLazySearchEmployeesQuery } from "../../users/employeesApi";

/**
 * Who the sale BELONGS to - the employee the commission is paid to.
 *
 * This is an EMPLOYEE search, not a user-account list (Batch152). The point of
 * the field is that an order can be entered by one person on behalf of an
 * employee who has no computer, or no access to the Sales module: the commission
 * goes to that employee, while CreatedBy still records whoever typed it. So the
 * value is an Employee_Details.RecordGuid, and an employee with no login is a
 * perfectly valid choice - the previous version could only offer people holding
 * a login, because it saved SalespersonUserId.
 *
 * Same idiom as EmployeePicker deliberately: an Employee Code box that jumps
 * straight to a person on Enter, plus name search under it. That pattern is
 * already used on Employees, Vehicles and the technician pickers, so this field
 * now behaves like every other "pick a person" field in the app rather than
 * being the odd one out. It reuses the very same /employees/search endpoint
 * (branch-scoped to the caller, no extra permission), so there is no new API
 * surface behind this.
 */
const toOption = (emp) => ({
  value: emp.recordGuid,
  label: `${emp.empName} ${emp.empLastName || ""} · ${emp.empCode}`,
  employee: emp,
});

const SalespersonPicker = ({ value, onChange, isDisabled = false }) => {
  const [triggerSearch] = useLazySearchEmployeesQuery();
  const [empCode, setEmpCode] = useState("");
  const [isSearchingCode, setIsSearchingCode] = useState(false);

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length === 0) return [];
    const { data = [] } = await triggerSearch(inputValue.trim());
    return data.map(toOption);
  };

  const handleCodeSearch = async () => {
    const code = empCode.trim();
    if (!code) return;
    setIsSearchingCode(true);
    try {
      const { data = [] } = await triggerSearch(code);
      // Exact code wins; fall back to the first hit so a partial code still
      // gets somewhere rather than silently doing nothing.
      const match = data.find((emp) => String(emp.empCode) === code) || data[0];
      if (match) {
        onChange(toOption(match));
        setEmpCode("");
      } else {
        toast.error(`No employee found for code ${code}`);
      }
    } finally {
      setIsSearchingCode(false);
    }
  };

  // One row: code + Find, then the name search taking the rest (2026-09-12, by
  // instruction). flex-wrap only drops the name search to its own line where
  // the space is genuinely too narrow (the list filter panels).
  return (
    <div className="d-flex flex-wrap gap-2 align-items-start">
      <div className="d-flex gap-2" style={{ flex: "0 0 auto" }}>
        <input
          type="text"
          className="form-control"
          placeholder="Code + Enter"
          style={{ width: 130 }}
          value={empCode}
          onChange={(e) => setEmpCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleCodeSearch();
            }
          }}
          disabled={isSearchingCode || isDisabled}
        />
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleCodeSearch}
          disabled={isSearchingCode || isDisabled || !empCode.trim()}
        >
          {isSearchingCode ? "..." : "Find"}
        </button>
      </div>
      <div style={{ flex: "1 1 220px", minWidth: 220 }}>
      <AsyncSelect
        classNamePrefix="react-select"
        cacheOptions
        isClearable
        isDisabled={isDisabled}
        loadOptions={loadOptions}
        value={value}
        onChange={onChange}
        placeholder="...or search by name"
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "No matching employees" : "Type a name, or enter a code"
        }
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
      </div>
    </div>
  );
};

export default SalespersonPicker;
